/**
 * The staff console's shell.
 *
 * A visitor who is not staff is answered as though this did not exist: the API
 * returns 404 rather than 403, because the existence of a staff console is not
 * something an ordinary customer needs confirmed. This mirrors that.
 */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import Brand from '../../components/site/Brand';
import Spinner from '../../components/Spinner';
import { goSignIn, useSession } from '../../lib/session';

export default function ConsoleLayout({
  children,
  back,
}: {
  children: ReactNode;
  back?: { to: string; label: string };
}) {
  const { identity, loading, signOut } = useSession();
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!identity?.signedIn) {
      goSignIn(location.pathname + location.search);
      return;
    }
    setChecked(true);
  }, [loading, identity?.signedIn, location.pathname, location.search]);

  if (loading || !checked) return <Spinner full label="Checking your access" />;

  // Not staff: the API will answer 404 for everything below, so say so once here
  // rather than letting every panel discover it separately.
  if (!identity?.user?.isPlatformAdmin) {
    return (
      <div className="site flex min-h-screen items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg">Nothing to see here</h1>
          <p className="mt-3 text-[13.5px] leading-relaxed text-bone/45">
            This page is not available to your account.
          </p>
          <Link to="/app" className="site-btn site-btn-ghost mt-6">
            Your workspaces
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="site min-h-screen">
      <header className="border-b border-veil/[0.07]">
        <div className="mx-auto flex max-w-page flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <div className="flex items-center gap-7">
            <Link to="/console">
              <Brand />
            </Link>
            <nav className="flex items-center gap-1">
              {[
                { to: '/console', end: true, label: 'Estate' },
                { to: '/console/people', end: false, label: 'People' },
                { to: '/console/library', end: false, label: 'Library' },
              ].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [
                      'rounded-full px-3 py-1.5 text-[12.5px] transition-colors',
                      isActive
                        ? 'bg-veil/[0.08] text-bone'
                        : 'text-bone/45 hover:bg-veil/[0.05] hover:text-bone',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/app"
              className="rounded-full px-3 py-1.5 text-[12.5px] text-bone/45 hover:bg-veil/[0.05] hover:text-bone"
            >
              Your workspaces
            </Link>
            <button type="button" onClick={() => void signOut()} className="site-btn site-btn-ghost">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="border-b border-veil/[0.05] bg-veil/[0.015] px-5 py-2 text-center font-code text-[11px] uppercase tracking-[0.16em] text-bone/30 sm:px-8">
        Platform staff · no candidate data appears here
      </div>

      <main className="mx-auto max-w-page px-5 py-10 sm:px-8">
        {back && (
          <Link
            to={back.to}
            className="mb-6 inline-flex items-center gap-1.5 font-code text-[11px] uppercase tracking-[0.16em] text-bone/35 hover:text-bone/70"
          >
            <span aria-hidden>←</span> {back.label}
          </Link>
        )}
        {children}
      </main>
    </div>
  );
}
